import type { ChannelData } from '~publish/legacy/composer/composer/entities/Draft'
import type { UserTag } from '~publish/legacy/composer/composer/stores/types'
import type { LinkedInAnnotationEntity } from '~publish/legacy/editor/plugins/linkedin-annotations/nodes'
import type { FacebookMentionDbEntry } from '~publish/legacy/editor/plugins/facebook-mention/nodes'
import {
  PostTypeCarousel,
  PostTypeEvent,
  PostTypeFacebookReel,
  PostTypeFacebookStory,
  PostTypeGhostPost,
  PostTypeOffer,
  PostTypePost,
  PostTypeReel,
  PostTypeShort,
  PostTypeStory,
  PostTypeThread,
  PostTypeWhatsNew,
} from '~publish/legacy/post/constants'
import { UpdateStatus } from '~publish/legacy/post/types'
import type {
  RpcUpdate,
  RpcUpdateCommonContent,
  RpcUpdateDocument,
  RpcUpdateImage,
  RpcUpdateLinkMedia,
  RpcUpdateMedia,
  RpcUpdateRetweetData,
  RpcUpdateVideo,
} from '~publish/legacy/post/types'

import type { ComposerPost } from './GetPostForComposer'

type Metadata = NonNullable<ComposerPost['metadata']>
type MetadataOf<T extends Metadata['__typename']> = Extract<
  Metadata,
  { __typename: T }
>
type Asset = ComposerPost['assets'][number]
type ThreadedPost = MetadataOf<'BlueskyPostMetadata'>['thread'][number]
type LinkAttachment = NonNullable<
  MetadataOf<'BlueskyPostMetadata'>['linkAttachment']
>
type Annotation = MetadataOf<'LinkedInPostMetadata'>['annotations'][number]
type PostType = Metadata['type']

const STATUS_TO_LEGACY: Record<ComposerPost['status'], UpdateStatus> = {
  draft: UpdateStatus.Draft,
  needs_approval: UpdateStatus.Draft,
  scheduled: UpdateStatus.Buffer,
  sending: UpdateStatus.Pending,
  sent: UpdateStatus.Sent,
  error: UpdateStatus.Error,
}

const toUnixSeconds = (dateTime: string): number =>
  Math.floor(new Date(dateTime).getTime() / 1000)

const toUtcDayUnixSeconds = (dateTime: string): number => {
  const date = new Date(dateTime)
  date.setUTCHours(0, 0, 0, 0)
  return Math.floor(date.getTime() / 1000)
}

const toUtcTime = (dateTime: string): string =>
  new Date(dateTime).toISOString().slice(11, 16)

const imageToRpcMedia = (
  asset: Extract<Asset, { __typename: 'ImageAsset' }>,
): RpcUpdateImage & { service_user_tags?: UserTag[] } => {
  const { altText, width, height, userTags } = asset.image
  return {
    progress: '100',
    uploaded: 'true',
    thumbnail: asset.thumbnail,
    picture: asset.source,
    photo: asset.source,
    alt_text: altText || null,
    width: width > 0 ? String(width) : '',
    height: height > 0 ? String(height) : '',
    ...(userTags?.length
      ? {
          service_user_tags: userTags.map(({ handle, x, y }) => ({
            username: handle,
            x: String(x),
            y: String(y),
          })),
        }
      : {}),
  }
}

const videoToRpcMedia = (
  asset: Extract<Asset, { __typename: 'VideoAsset' }>,
): RpcUpdateVideo => {
  const { video } = asset
  return {
    progress: '100',
    uploaded: 'true',
    thumbnail: asset.thumbnail,
    uploading_type: 'video',
    video: {
      id: asset.id ?? '',
      title: video.title ?? '',
      thumbnails: asset.thumbnail ? [asset.thumbnail] : [],
      thumb_offset: video.thumbnailOffset ?? undefined,
      details: {
        duration: video.durationMs / 1000,
        duration_millis: video.durationMs,
        // core-api leaves `fileSize` unset; 0 reads as "unknown" downstream,
        // as in mapDraftContentToPrefillMedia.
        file_size: video.fileSize ?? 0,
        width: video.width,
        height: video.height,
        // `null` tells the composer the video has no audio track; core-api
        // never fills `audioCodec`, so a missing value must stay "unknown".
        audio_codec: video.audioCodec ?? undefined,
        // `source` is the transcoded file when one exists, else the original.
        transcoded_location: asset.source,
        location: asset.source,
      },
    },
  }
}

const documentToRpcMedia = (
  asset: Extract<Asset, { __typename: 'DocumentAsset' }>,
): RpcUpdateDocument => ({
  progress: '100',
  uploaded: 'true',
  thumbnail: asset.thumbnail,
  uploading_type: 'document',
  document: {
    id: asset.id ?? '',
    title: asset.document.title ?? '',
    details: {
      location: asset.source,
      pageCount: asset.document.numPages,
      fileSizeBytes: asset.document.filesize ?? undefined,
    },
  },
})

const assetToRpcMedia = (asset: Asset): RpcUpdateMedia | null => {
  switch (asset.__typename) {
    case 'ImageAsset':
      return imageToRpcMedia(asset)
    case 'VideoAsset':
      return videoToRpcMedia(asset)
    case 'DocumentAsset':
      return documentToRpcMedia(asset)
    default:
      return null
  }
}

const linkToRpcMedia = (link: LinkAttachment): RpcUpdateLinkMedia => ({
  link: link.url,
  expanded_link: link.expandedUrl || undefined,
  title: link.title,
  description: link.text,
  preview: link.thumbnail ?? '',
  original_preview: link.thumbnail ?? '',
  thumbnail: link.thumbnail ?? '',
})

/**
 * The legacy shape keeps a link attachment in `media` with nothing else
 * attached; otherwise `media` is the first asset and `extra_media` the rest.
 */
const toRpcMediaFields = (
  assets: readonly Asset[],
  link: LinkAttachment | null | undefined,
): Pick<RpcUpdateCommonContent, 'media' | 'extra_media'> => {
  if (link) return { media: linkToRpcMedia(link), extra_media: [] }

  const [media, ...extraMedia] = assets.flatMap((asset) => {
    const item = assetToRpcMedia(asset)
    return item ? [item] : []
  })
  return { media, extra_media: extraMedia }
}

const threadedPostToRpc = (item: ThreadedPost): RpcUpdateCommonContent => ({
  text: item.text,
  links: [],
  ...toRpcMediaFields(
    item.assets,
    item.metadata?.bluesky?.linkAttachment ??
      item.metadata?.threads?.linkAttachment,
  ),
})

const getThread = (metadata: Metadata | null): ThreadedPost[] => {
  if (!metadata || !('thread' in metadata)) return []
  return metadata.thread
}

const getRootLinkAttachment = (
  metadata: Metadata | null,
): LinkAttachment | null | undefined =>
  metadata && 'linkAttachment' in metadata ? metadata.linkAttachment : null

/**
 * `metadata.type` folds the stored `update_type` into network-neutral values,
 * and reports a stored `thread` as `post`. A post that carries thread items is
 * a thread, the way the native create path stores it.
 */
const toLegacyUpdateType = (
  metadata: Metadata | null,
  hasThread: boolean,
): string | undefined => {
  if (!metadata) return undefined
  const type: PostType = metadata.type

  if (type === 'ghost_post') return PostTypeGhostPost
  // The composer edits a Threads post only as `thread` or `ghost_post`.
  if (hasThread || metadata.__typename === 'ThreadsPostMetadata') {
    return PostTypeThread
  }

  switch (type) {
    case 'reel':
      return metadata.__typename === 'FacebookPostMetadata'
        ? PostTypeFacebookReel
        : PostTypeReel
    case 'story':
      return metadata.__typename === 'FacebookPostMetadata'
        ? PostTypeFacebookStory
        : PostTypeStory
    case 'short':
      return PostTypeShort
    case 'carousel':
      return PostTypeCarousel
    case 'whats_new':
      return PostTypeWhatsNew
    case 'offer':
      return PostTypeOffer
    case 'event':
      return PostTypeEvent
    case 'thread':
      return PostTypeThread
    default:
      return PostTypePost
  }
}

const getGoogleBusinessChannelData = (
  metadata: MetadataOf<'GoogleBusinessPostMetadata'>,
): NonNullable<ChannelData['googlebusiness']> => {
  const { details } = metadata
  switch (details?.__typename) {
    case 'GoogleBusinessOfferMetaData':
      return {
        title: details.title,
        start_date: toUnixSeconds(details.startDate),
        end_date: toUnixSeconds(details.endDate),
        code: details.code,
        link: details.link,
        terms: details.terms,
      }
    case 'GoogleBusinessEventMetaData':
      // The composer keeps the event day as a UTC midnight and the time
      // separately; core-api merges the time into the date's UTC hours.
      return {
        title: details.title,
        cta: details.button,
        link: details.link,
        start_date: toUtcDayUnixSeconds(details.startDate),
        end_date: toUtcDayUnixSeconds(details.endDate),
        start_time: details.isFullDayEvent
          ? undefined
          : toUtcTime(details.startDate),
        end_time: details.isFullDayEvent
          ? undefined
          : toUtcTime(details.endDate),
      }
    case 'GoogleBusinessWhatsNewMetaData':
      return { title: metadata.title, cta: details.button, link: details.link }
    default:
      return { title: metadata.title }
  }
}

/**
 * Rebuilds the per-network `channel_data` bag the RPC returned. A field the
 * post never stored stays absent, as it was in the raw RPC bag, since the
 * composer replaces its channel data with this object wholesale.
 */
const getChannelData = (metadata: Metadata | null): ChannelData | undefined => {
  if (!metadata) return undefined

  switch (metadata.__typename) {
    case 'InstagramPostMetadata': {
      const { geolocation, stickerFields, audio } = metadata
      return {
        instagram: {
          comment_text: metadata.firstComment ?? undefined,
          link: metadata.link ?? undefined,
          share_to_feed: metadata.shouldShareToFeed,
          is_ai_generated: metadata.isAiGenerated,
          collaborators: metadata.collaborators.length
            ? metadata.collaborators
            : undefined,
          trial_reel_graduation_strategy:
            metadata.trialReelGraduationStrategy ?? undefined,
          service_geolocation_id: geolocation?.id ?? undefined,
          service_geolocation_name: geolocation?.text ?? undefined,
          text: stickerFields?.text ?? undefined,
          music: stickerFields?.music ?? undefined,
          products: stickerFields?.products ?? undefined,
          topics: stickerFields?.topics ?? undefined,
          other: stickerFields?.other ?? undefined,
          audio: audio
            ? {
                audio_id: audio.id,
                audio_type:
                  audio.type === 'originalSound' ? 'original_sound' : 'music',
                title: audio.title,
                display_artist: audio.displayArtist ?? undefined,
                creator_username: audio.creatorUsername ?? undefined,
                duration_in_ms: audio.duration,
                audio_volume: audio.audioVolume,
                video_volume: audio.videoVolume,
              }
            : undefined,
        },
      }
    }
    case 'FacebookPostMetadata':
      return {
        facebook: {
          comment_text: metadata.firstComment ?? undefined,
          ...(metadata.title ? { title: metadata.title } : {}),
        },
      }
    case 'LinkedInPostMetadata':
      return {
        linkedin: { comment_text: metadata.firstComment ?? undefined },
      }
    case 'TwitterPostMetadata':
      return { twitter: { is_ai_generated: metadata.isAiGenerated } }
    case 'GoogleBusinessPostMetadata':
      return { googlebusiness: getGoogleBusinessChannelData(metadata) }
    case 'YoutubePostMetadata': {
      const youtube: Partial<NonNullable<ChannelData['youtube']>> = {
        title: metadata.title ?? undefined,
        privacyStatus: metadata.privacy ?? undefined,
        license: metadata.license ?? undefined,
        category: metadata.category
          ? { id: metadata.category.categoryId, title: metadata.category.title }
          : undefined,
        notifySubscribers: metadata.notifySubscribers,
        embeddable: metadata.embeddable,
        madeForKids: metadata.madeForKids,
        is_ai_generated: metadata.isAiGenerated,
      }
      // Same partial bag the RPC handed over for a post saved without every
      // YouTube field; the composer reads each one optionally.
      return { youtube: youtube as NonNullable<ChannelData['youtube']> }
    }
    case 'MastodonPostMetadata':
      return {
        mastodon: { spoilerText: metadata.spoilerText ?? undefined },
      }
    case 'StartPagePostMetadata':
      return { startPage: { link: metadata.link ?? undefined } }
    case 'TiktokPostMetadata':
      return {
        tiktok: {
          title: metadata.title ?? undefined,
          is_ai_generated: metadata.isAiGenerated,
        },
      }
    case 'ThreadsPostMetadata': {
      const { topic, locationId, locationName } = metadata
      if (topic == null && locationId == null && locationName == null) {
        return {}
      }
      return {
        threads: {
          topic: topic ?? undefined,
          location_id: locationId ?? undefined,
          location_name: locationName ?? undefined,
        },
      }
    }
    default:
      return {}
  }
}

const getLinkedInAnnotations = (
  annotations: readonly Annotation[],
): LinkedInAnnotationEntity[] =>
  annotations
    .filter((annotation) => annotation.type === 'annotation')
    .map(({ content, text, url, indices: [start, end] }) => ({
      // core-api drops `id` and `vanityName`; the URN and profile URL hold
      // both. Publishing reads only `entity`.
      id: content.split(':').pop() ?? content,
      entity: content,
      localizedName: text,
      link: url,
      vanityName: getLastPathSegment(url),
      start,
      length: end - start,
    }))

const getLastPathSegment = (url: string): string => {
  try {
    return new URL(url).pathname.split('/').findLast(Boolean) ?? ''
  } catch {
    return ''
  }
}

const getFacebookMentions = (
  annotations: readonly Annotation[],
): FacebookMentionDbEntry[] =>
  annotations
    .filter((annotation) => annotation.type === 'mention')
    .map(({ content, text, url, indices: [start, end] }) => ({
      indices: [start, end],
      content,
      text,
      url,
    }))

const getRetweet = (
  metadata: Metadata | null,
  comment: string,
): RpcUpdateRetweetData | undefined => {
  if (metadata?.__typename !== 'TwitterPostMetadata' || !metadata.retweet) {
    return undefined
  }
  const { retweet } = metadata
  return {
    tweet_id: retweet.id,
    // Not exposed by the API; the composer sends only the tweet id back.
    user_id: '',
    username: retweet.user.username,
    profile_name: retweet.user.name,
    url: retweet.url,
    text: retweet.text,
    avatars: { http: retweet.user.avatar, https: retweet.user.avatar },
    created_at: toUnixSeconds(retweet.createdAt),
    // The native save stores the quote comment as both `retweet.comment` and
    // the post text.
    comment,
  }
}

/**
 * Maps the GraphQL post to the `RpcUpdate` fields the composer reads when it
 * opens a post for editing or duplication (see `DataImportUtils.formatMetaData`
 * and `LoadLegacyStores`). Fields only post cards read get neutral values.
 */
export function postToRpcUpdate(post: ComposerPost): RpcUpdate {
  const { metadata } = post
  const thread = getThread(metadata)
  const hasThread = thread.length > 0
  const status = STATUS_TO_LEGACY[post.status]
  const isReminder = post.schedulingType === 'notification'
  const dueAt = post.dueAt ? toUnixSeconds(post.dueAt) : 0
  const scheduledAt = post.isCustomScheduled && dueAt ? dueAt : undefined
  const retweet = getRetweet(metadata, post.text)
  const annotations =
    metadata?.__typename === 'LinkedInPostMetadata' ||
    metadata?.__typename === 'FacebookPostMetadata'
      ? metadata.annotations
      : []

  return {
    id: post.id,
    profileId: post.channelId,
    profile_service: post.channelService,
    subprofile_id:
      metadata?.__typename === 'PinterestPostMetadata'
        ? (metadata.board?.id ?? undefined)
        : undefined,
    status,
    isDraft: status === UpdateStatus.Draft,
    needsApproval: post.status === 'needs_approval',
    isSent: post.status === 'sent',
    isReminder,
    createdAt: toUnixSeconds(post.createdAt),
    due_at: dueAt,
    scheduled_at: scheduledAt,
    scheduledAt,
    text: post.text,
    title:
      metadata?.__typename === 'PinterestPostMetadata'
        ? (metadata.title ?? undefined)
        : undefined,
    source_url:
      metadata?.__typename === 'PinterestPostMetadata'
        ? (metadata.url ?? undefined)
        : undefined,
    updateType: toLegacyUpdateType(metadata, hasThread),
    channelData: getChannelData(metadata),
    ideaId: post.ideaId ?? undefined,
    tags: post.tags.map(({ id, name, color }) => ({ id, name, color })),
    ...toRpcMediaFields(post.assets, getRootLinkAttachment(metadata)),
    thread: hasThread ? thread.map(threadedPostToRpc) : undefined,
    retweet,
    annotations:
      metadata?.__typename === 'LinkedInPostMetadata'
        ? getLinkedInAnnotations(annotations)
        : undefined,
    entities:
      metadata?.__typename === 'FacebookPostMetadata'
        ? getFacebookMentions(annotations)
        : undefined,
    links: [],
    day: '',
    type: '',
    isPastDue: false,
    isPastReminder: false,
    isFixed: false,
    pinned: false,
    imageUrls: [],
    profileTimezone: '',
    linkAttachment: {},
    statistics: {},
    user: { email: '', avatar: '', id: '' },
    postContent: { text: post.text, type: '' },
    postDetails: {
      postAction: '',
      isRetweet: !!retweet,
      isCustomScheduled: post.isCustomScheduled,
      isInstagramReminder: isReminder,
      isPendingTiktokPost: false,
    },
  }
}
